import { useCallback, useEffect, useState } from 'react';
import { ICreateProjectSendData } from '@/providers/create-project/types';
import { stateSetter } from '@/utils';
import { useAuthContext } from '@/providers';
import { P } from '@/components/permission-gate/permissions';
import { hasPermission } from '@/components/permission-gate/use-has-permission';
import {
	deleteProject,
	fetchPosterQr,
	fetchProjects,
	fetchProjectsList,
	fetchQueryProjects,
	fetchSingleProject,
	patchProject,
	postProject,
	postProjectGeometry,
	postProjectName
} from './actions';
import {
	ICheckProjectName,
	IProject,
	IProjectListItem,
	IProjectsListQuery,
	ProjectsReturnType
} from './types';

// Fallback stabil ca identitate pentru `data` neîncărcat — un `[]` nou la fiecare
// randare re-declanșează în lanț efectele consumatorilor cu dependență pe `data`
// („Maximum update depth exceeded").
const EMPTY_LIST: never[] = [];

export function useProjects<T extends IProject | IProject[]>(
	getProjectsOnRender: boolean | undefined = undefined,
	idParam: number | undefined = undefined,
	queryParam: string | undefined = undefined,
	getAllQuery: boolean | undefined = undefined,
	getAllProj: boolean | undefined = undefined
): ProjectsReturnType<T> {
	const { user } = useAuthContext();
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingCTA, setLoadingCTA] = useState<boolean>(false);
	const [enableQuery, setEnableQuery] = useState<boolean>(false);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(10);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(1);
	const [projectsList, setProjectsList] = useState<IProjectListItem[]>([]);
	const [loadingProjectsList, setLoadingProjectsList] = useState<boolean>(false);

	const handleResponse = (pageParam: number, itemsParam: number): void => {
		setTotalPages(pageParam);
		setTotalItems(itemsParam);
	};

	// GET api/projects (contract nou) — listă pentru picker-ul / filtrele de proiect din anunțuri.
	// Fără `projects:read` (ex. moderatorul de cartier, care lucrează doar pe cartierul lui)
	// lista rămâne goală: cererea ar pica oricum cu 403 și ar umple ecranul de erori.
	const canReadProjects = hasPermission(user, P.projectsRead);
	const getProjectsList = useCallback(async (query?: IProjectsListQuery) => {
		if (!canReadProjects) {
			setProjectsList([]);
			return [];
		}
		try {
			setLoadingProjectsList(true);
			const response = await fetchProjectsList(query);
			setProjectsList(response);
			return response;
		} catch {
			return [];
		} finally {
			setLoadingProjectsList(false);
		}
	}, [canReadProjects]);

	const getProjects = useCallback(
		async (allProj: boolean, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchProjects(
					allProj,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getQueryProjects = useCallback(
		async (allQuery: boolean, query: string, queryPage: number, queryItems: number) => {
			try {
				!enableQuery && setEnableQuery(true);
				setLoading(true);
				const response = await fetchQueryProjects(
					allQuery,
					query,
					queryPage,
					queryItems,
					handleResponse
				);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getProject = useCallback(
		async (projId: number) => {
			try {
				setLoading(true);
				const response = await fetchSingleProject(projId);
				setData(response as T);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const createProject = useCallback(
		async (
			projectObject: ICreateProjectSendData,
			onSuccess?: () => void,
			onError?: () => void
		) => {
			try {
				setLoadingCTA(true);
				const response = await postProject(projectObject);
				if (response === 201) {
					onSuccess && onSuccess();
				} else if (response === 400) {
					onError && onError();
				}
			} catch {
				onError && onError();
			} finally {
				setLoadingCTA(false);
			}
		},
		 
		[setData]
	);

	const updateProject = useCallback(
		async (
			projId: number,
			projectObject: Omit<ICreateProjectSendData, 'elements'>,
			onSuccess?: () => void,
			onError?: () => void
		) => {
			try {
				setLoadingUpdate(true);
				const response = await patchProject(projId, projectObject);
				if (response === 200) {
					onSuccess && onSuccess();
				} else if (response === 400) {
					onError && onError();
				}
			} catch {
				onError && onError();
			} finally {
				setLoadingUpdate(false);
			}
		},
		 
		[setData]
	);

	const removeProject = useCallback(
		async (projectId: number, onSuccess?: () => void) => {
			try {
				setLoading(true);
				const response = await deleteProject(projectId);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		 
		[setData]
	);

	const getProjectPoster = useCallback(async (id: number) => {
		try {
			setLoadingCTA(true);
			const response = await fetchPosterQr(id);
			if (response.status !== 200) {
				throw new Error('Nu am reușit să accesăm posterul');
			}
			const fileUrl = window.URL.createObjectURL(response.data);
			window.open(fileUrl, '_blank');
		} catch {
			// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
		} finally {
			setLoadingCTA(false);
		}
	}, []);

	const checkProjectName = useCallback(
		async (
			params: ICheckProjectName,
			onSuccess?: () => void,
			onError?: () => void,
				fsp?: number
		) => {
			try {
				const response = await postProjectName(params, fsp);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch (error) {
				const message = error as unknown as { message: string };
				console.log(`${message?.message}`);
				onError && onError();
			}
		},
		 
		[setData]
	);

	const checkProjectGeometry = useCallback(
		async (params: any, onSuccess?: () => void, onError?: () => void, fsp?: number) => {
			try {
				const response = await postProjectGeometry(params, fsp);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch (error) {
				const message = error as unknown as { message: string };
				console.log(`${message?.message}`);
				onError && onError();
			}
		},
		 
		[setData]
	);

	useEffect(() => {
		if (getProjectsOnRender && queryParam === undefined) {
			getProjects(getAllProj || false, page, items);
		}
		 
	}, [getProjectsOnRender, queryParam, page, items, getAllProj]);

	useEffect(() => {
		if (idParam) {
			getProject(idParam);
		}
		 
	}, [idParam]);

	useEffect(() => {
		if (queryParam && idParam === undefined) {
			getQueryProjects(getAllQuery || false, queryParam, page, items);
		} else if (idParam === undefined && enableQuery) {
			getProjects(getAllProj || false, page, items);
		}
		 
	}, [queryParam, idParam, page, items, getAllProj, getAllQuery]);

	return {
		data: data || (EMPTY_LIST as IProject[] as T),
		setData: setData as stateSetter<T>,
		loading,
		getProjects,
		createProject,
		getProject,
		removeProject,
		loadingUpdate,
		loadingCTA,
		updateProject,
		getProjectPoster,
		checkProjectName,
		checkProjectGeometry,
		items,
		page,
		setItems,
		setPage,
		totalPages,
		totalItems,
		projectsList,
		loadingProjectsList,
		getProjectsList
	};
}
